/**
 * Apply a validated v1 snapshot to the persistence layer.
 *
 * Branches:
 *   - clear              → mint a fresh StationId, write everything fresh.
 *   - Replace            → keep the existing StationId, wipe per-section
 *                          data, then re-insert from the snapshot.
 *   - Save-as-copy       → mint a fresh StationId, override the snapshot's
 *                          (cpid, wsUrl) so the pair is unique.
 *
 * Each section is gated by a toggle (`include`); defaults match the YAML
 * (`include: true` for topology, configuration, chargingProfiles,
 * offlineQueue). The audit row is appended last so a partial failure does
 * not leave a misleading "import succeeded" entry behind.
 */
import {
  ChargingProfileId,
  ConnectorId,
  EvseId,
  TransactionId,
  type StationId,
} from '@/domain/types';
import type {
  ChargingProfileRepository,
  ConfigurationRepository,
  LocalAuthListEntryRow,
  LocalAuthListRepository,
  LocalAuthListVersionRepository,
  OfflineQueueRepository,
  SnapshotRepository,
  StationRepository,
  VariableValuesRepository,
} from '@/persistence/ports';
import type { ChargingProfileSnapshot } from '@/domain/ChargingProfile';
import type { ConfigurationKeySnapshot } from '@/domain/ConfigurationKey';
import type { OfflineSessionSnapshot } from '@/domain/OfflineSession';
import type { StationSnapshot } from '@/domain/Station';
import { OfflineSessionStatus } from '@/domain/types';
import { newStationId } from '@/domain/newStationId';
import { ConflictResolution, type ConflictChoice, type ConflictDetected } from './conflictResolver';
import type { SnapshotChargingProfileV1, SnapshotV1 } from './snapshotSchema';

export interface ImportRepos {
  readonly stations: StationRepository;
  readonly configurations: ConfigurationRepository;
  readonly chargingProfiles: ChargingProfileRepository;
  readonly offlineQueue: OfflineQueueRepository;
  readonly snapshots: SnapshotRepository;
  // v2 sections
  readonly variableValues: VariableValuesRepository;
  readonly localAuthList: LocalAuthListRepository;
  readonly localAuthListVersion: LocalAuthListVersionRepository;
}

export interface ImportOptions {
  readonly include?: {
    readonly topology?: boolean;
    readonly configuration?: boolean;
    readonly chargingProfiles?: boolean;
    readonly offlineQueue?: boolean;
    readonly deviceModel?: boolean;
    readonly localAuthList?: boolean;
  };
  /**
   * What to do when (cpid, wsUrl) already exists. If omitted, a clear
   * import is assumed; callers should always run `detectConflict` first.
   */
  readonly conflict?: { readonly conflict: ConflictDetected; readonly choice: ConflictChoice };
  readonly now?: () => number;
  /**
   * Test seam — when set, replaces newStationId() with a deterministic
   * factory. Real callers leave this undefined.
   */
  readonly stationIdFactory?: () => StationId;
}

export interface ImportReport {
  readonly stationId: StationId;
  readonly cpid: string;
  readonly wsUrl: string;
  readonly counts: {
    readonly configurationKeys: number;
    readonly chargingProfiles: number;
    readonly offlineQueue: number;
    readonly variableValues: number;
    readonly localAuthEntries: number;
  };
  readonly replaced: boolean;
}

/**
 * Apply the v2 sections (2.0.1 Device Model + Local Auth List) onto a station.
 * Wipe-then-write so it is correct for fresh imports (clear is a no-op),
 * Replace, and ApplyToOpen alike. v1 snapshots carry neither → no-op.
 */
async function applyV2Sections(
  stationId: StationId,
  snap: SnapshotV1,
  include: { readonly deviceModel: boolean; readonly localAuthList: boolean },
  repos: ImportRepos,
  now: () => number,
): Promise<{ variableValues: number; localAuthEntries: number }> {
  let variableValues = 0;
  if (include.deviceModel && snap.station.variableValues) {
    await repos.variableValues.clear(stationId);
    for (const v of snap.station.variableValues) {
      await repos.variableValues.upsert(stationId, {
        component: v.component,
        variable: v.variable,
        evseId: v.evseId,
        connectorId: v.connectorId,
        attributeType: v.attributeType,
        value: v.value,
        mutability: v.mutability,
        dataType: v.dataType,
        ...(v.unit !== undefined ? { unit: v.unit } : {}),
        section: v.section,
      });
      variableValues++;
    }
  }
  let localAuthEntries = 0;
  if (include.localAuthList && snap.station.localAuthList) {
    const rows: LocalAuthListEntryRow[] = snap.station.localAuthList.entries.map((e) => ({
      stationId,
      idTag: e.idTag,
      status: e.status,
      ...(e.expiryDate !== undefined ? { expiryDate: e.expiryDate } : {}),
      ...(e.parentIdTag !== undefined ? { parentIdTag: e.parentIdTag } : {}),
      updatedAt: now(),
    }));
    await repos.localAuthList.replaceAll(stationId, rows);
    await repos.localAuthListVersion.set(stationId, snap.station.localAuthList.version);
    localAuthEntries = rows.length;
  }
  return { variableValues, localAuthEntries };
}

export async function applySnapshot(
  snap: SnapshotV1,
  repos: ImportRepos,
  opts: ImportOptions = {},
): Promise<ImportReport> {
  const now = opts.now ?? Date.now;
  const mint = opts.stationIdFactory ?? newStationId;
  const choice = opts.conflict?.choice;

  if (choice?.resolution === ConflictResolution.Cancel) {
    throw new Error('Import cancelled.');
  }

  // FEAT-007: ApplyToOpen is identity-preserving — branch entirely out
  // of the normal "write a station row" path. We replace toggled sections
  // on the target stationId and never touch the stations / topology row.
  if (choice?.resolution === ConflictResolution.ApplyToOpen) {
    const targetId = choice.targetStationId as StationId;
    const existing = await repos.stations.get(targetId);
    if (!existing) {
      throw new Error(`ApplyToOpen: target station ${targetId} not found.`);
    }
    const includeApplied = {
      configuration: choice.include?.configuration !== false,
      chargingProfiles: choice.include?.chargingProfiles !== false,
      offlineQueue: choice.include?.offlineQueue !== false,
      deviceModel: choice.include?.deviceModel !== false,
      localAuthList: choice.include?.localAuthList !== false,
    };
    let configCountApplied = 0;
    if (includeApplied.configuration) {
      for (const k of await repos.configurations.byStation(targetId)) {
        await repos.configurations.delete(targetId, k.key);
      }
      for (const k of snap.station.configuration) {
        await repos.configurations.upsert(targetId, {
          key: k.key,
          value: k.value,
          readonly: k.readonly,
          type: k.type,
          section: k.section,
        });
        configCountApplied++;
      }
    }
    let profileCountApplied = 0;
    if (includeApplied.chargingProfiles) {
      await repos.chargingProfiles.clear(targetId);
      for (const p of snap.station.chargingProfiles) {
        await repos.chargingProfiles.upsert(targetId, fromProfileV1(p));
        profileCountApplied++;
      }
    }
    let offlineCountApplied = 0;
    if (includeApplied.offlineQueue) {
      for (const existingRow of await repos.offlineQueue.byStation(targetId)) {
        await repos.offlineQueue.delete(targetId, existingRow.queueId);
      }
      for (const s of snap.station.offlineQueue) {
        const offSnap: OfflineSessionSnapshot = {
          queueId:
            globalThis.crypto?.randomUUID?.() ?? `queue-${now()}-${offlineCountApplied}`,
          idTag: s.idTag,
          connectorId: ConnectorId.of(s.connectorId),
          startedAt: Date.parse(s.startedAt),
          duration: s.duration,
          meterStartWh: s.meterStartWh,
          energyDeliveredWh: s.energyDeliveredWh,
          meterValueIntervalSec: s.meterValueIntervalSec,
          ...(s.startSoC !== undefined ? { startSoC: s.startSoC } : {}),
          ...(s.endSoC !== undefined ? { endSoC: s.endSoC } : {}),
          voltage: s.voltage,
          stopReason: s.stopReason,
          status: OfflineSessionStatus.Queued,
        };
        await repos.offlineQueue.upsert(targetId, offSnap);
        offlineCountApplied++;
      }
    }
    const v2Applied = await applyV2Sections(
      targetId,
      snap,
      { deviceModel: includeApplied.deviceModel, localAuthList: includeApplied.localAuthList },
      repos,
      now,
    );
    const auditId =
      globalThis.crypto?.randomUUID?.() ?? `import-applied-${now()}`;
    await repos.snapshots.upsert({
      id: auditId,
      stationId: targetId,
      createdAt: now(),
      kind: 'import-applied',
      payload: snap,
    });
    return {
      stationId: targetId,
      cpid: existing.cpid,
      wsUrl: existing.wsUrl,
      counts: {
        configurationKeys: configCountApplied,
        chargingProfiles: profileCountApplied,
        offlineQueue: offlineCountApplied,
        variableValues: v2Applied.variableValues,
        localAuthEntries: v2Applied.localAuthEntries,
      },
      replaced: true,
    };
  }

  let stationId: StationId;
  let cpid = snap.station.cpid;
  let wsUrl = snap.station.wsUrl;
  let replaced = false;

  if (choice?.resolution === ConflictResolution.Replace && opts.conflict) {
    stationId = opts.conflict.conflict.existingStationId as StationId;
    replaced = true;
    // Per-section wipe before re-insert so old keys don't linger after a
    // partial overlap.
    await repos.chargingProfiles.clear(stationId);
    for (const existing of await repos.configurations.byStation(stationId)) {
      await repos.configurations.delete(stationId, existing.key);
    }
    for (const existing of await repos.offlineQueue.byStation(stationId)) {
      await repos.offlineQueue.delete(stationId, existing.queueId);
    }
  } else if (choice?.resolution === ConflictResolution.SaveAsCopy && opts.conflict) {
    stationId = mint();
    // The caller already validated overrides via applySaveAsCopy and routed
    // them through; we just pull them off the choice.
    if ('cpid' in choice && choice.cpid !== undefined && choice.cpid !== '') {
      cpid = choice.cpid;
    }
    if ('wsUrl' in choice && choice.wsUrl !== undefined && choice.wsUrl !== '') {
      wsUrl = choice.wsUrl;
    }
  } else {
    stationId = mint();
  }

  const include = {
    topology: opts.include?.topology !== false,
    configuration: opts.include?.configuration !== false,
    chargingProfiles: opts.include?.chargingProfiles !== false,
    offlineQueue: opts.include?.offlineQueue !== false,
    deviceModel: opts.include?.deviceModel !== false,
    localAuthList: opts.include?.localAuthList !== false,
  };

  const stationSnap: StationSnapshot = {
    id: stationId,
    cpid,
    wsUrl,
    displayName: snap.station.name,
    ocppVersion: snap.station.ocppVersion,
    topology: include.topology
      ? {
          cpMaxPowerW: snap.station.topology.cpMaxPowerW,
          evses: snap.station.topology.evses.map((e) => ({
            id: EvseId.of(e.id),
            name: e.name,
            maxPowerW: e.maxPowerW,
            connectorIds: e.connectorIds.map((c) => ConnectorId.of(c)),
          })),
        }
      : { cpMaxPowerW: 0, evses: [] },
    createdAt: now(),
  };
  await repos.stations.upsert(stationSnap);

  let configCount = 0;
  if (include.configuration) {
    for (const k of snap.station.configuration) {
      const keySnap: ConfigurationKeySnapshot = {
        key: k.key,
        value: k.value,
        readonly: k.readonly,
        type: k.type,
        section: k.section,
      };
      await repos.configurations.upsert(stationId, keySnap);
      configCount++;
    }
  }

  let profileCount = 0;
  if (include.chargingProfiles) {
    for (const p of snap.station.chargingProfiles) {
      await repos.chargingProfiles.upsert(stationId, fromProfileV1(p));
      profileCount++;
    }
  }

  let offlineCount = 0;
  if (include.offlineQueue) {
    for (const s of snap.station.offlineQueue) {
      const offSnap: OfflineSessionSnapshot = {
        queueId: globalThis.crypto?.randomUUID?.() ?? `queue-${now()}-${offlineCount}`,
        idTag: s.idTag,
        connectorId: ConnectorId.of(s.connectorId),
        startedAt: Date.parse(s.startedAt),
        duration: s.duration,
        meterStartWh: s.meterStartWh,
        energyDeliveredWh: s.energyDeliveredWh,
        meterValueIntervalSec: s.meterValueIntervalSec,
        ...(s.startSoC !== undefined ? { startSoC: s.startSoC } : {}),
        ...(s.endSoC !== undefined ? { endSoC: s.endSoC } : {}),
        voltage: s.voltage,
        stopReason: s.stopReason,
        status: OfflineSessionStatus.Queued,
      };
      await repos.offlineQueue.upsert(stationId, offSnap);
      offlineCount++;
    }
  }

  const v2 = await applyV2Sections(
    stationId,
    snap,
    { deviceModel: include.deviceModel, localAuthList: include.localAuthList },
    repos,
    now,
  );

  // Audit trail — append after every section so a partial failure surfaces
  // via the thrown error rather than a misleading "imported" row.
  const auditId = globalThis.crypto?.randomUUID?.() ?? `import-${now()}`;
  await repos.snapshots.upsert({
    id: auditId,
    stationId,
    createdAt: now(),
    kind: 'import',
    payload: snap,
  });

  return {
    stationId,
    cpid,
    wsUrl,
    counts: {
      configurationKeys: configCount,
      chargingProfiles: profileCount,
      offlineQueue: offlineCount,
      variableValues: v2.variableValues,
      localAuthEntries: v2.localAuthEntries,
    },
    replaced,
  };
}

function fromProfileV1(p: SnapshotChargingProfileV1): ChargingProfileSnapshot {
  return {
    id: ChargingProfileId.of(p.id),
    stackLevel: p.stackLevel,
    purpose: p.purpose,
    kind: p.kind,
    ...(p.recurrencyKind !== undefined ? { recurrencyKind: p.recurrencyKind } : {}),
    ...(p.validFrom !== undefined ? { validFrom: Date.parse(p.validFrom) } : {}),
    ...(p.validTo !== undefined ? { validTo: Date.parse(p.validTo) } : {}),
    ...(p.transactionId !== undefined
      ? { transactionId: TransactionId.of(p.transactionId) }
      : {}),
    connectorId: p.connectorId,
    schedule: {
      ...(p.schedule.startSchedule !== undefined
        ? { startSchedule: Date.parse(p.schedule.startSchedule) }
        : {}),
      ...(p.schedule.duration !== undefined ? { duration: p.schedule.duration } : {}),
      chargingRateUnit: p.schedule.chargingRateUnit,
      ...(p.schedule.minChargingRate !== undefined
        ? { minChargingRate: p.schedule.minChargingRate }
        : {}),
      chargingSchedulePeriod: p.schedule.chargingSchedulePeriod.map((pp) => ({
        startPeriod: pp.startPeriod,
        limit: pp.limit,
        ...(pp.numberPhases !== undefined ? { numberPhases: pp.numberPhases } : {}),
      })),
    },
  };
}
